import { useState } from 'react'
import {
  ChevronDown,
  Download,
  Link2,
  Loader2,
  Menu,
  MessageSquarePlus,
  Pencil,
  Search,
  Trash2,
  Users,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/Modal'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/cn'
import { downloadAuthenticatedFile } from '@/lib/downloadBlob'
import { useAuth } from '@/auth/useAuth'
import {
  useChatThreads,
  useCreateChatThread,
  useDeleteAllChatThreads,
  useDeleteChatThread,
  useRenameChatThread,
} from './queries'
import { ShareLinkDialog } from './ShareLinkDialog'
import { VisibilityPicker, type VisibilityValue } from './VisibilityPicker'
import type { ChatThreadSummary } from './types'

interface Props {
  activeThreadId: string | null
  onSelect: (id: string | null) => void
  onToggleCollapse?: () => void
  /** Visibility queued by the picker for the next "+ New chat" send. */
  pendingVisibility?: VisibilityValue | null
  onPendingVisibilityChange?: (v: VisibilityValue | null) => void
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime()
  const diff = Date.now() - then
  const mins = Math.round(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.round(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(iso).toLocaleDateString()
}

export function ThreadSidebar({
  activeThreadId,
  onSelect,
  onToggleCollapse,
  pendingVisibility,
  onPendingVisibilityChange,
}: Props) {
  const { hasPermission } = useAuth()
  const canShareAll = hasPermission('chatbot.share.all')
  const threadsQ = useChatThreads()
  const createM = useCreateChatThread()
  const renameM = useRenameChatThread()
  const deleteM = useDeleteChatThread()
  const deleteAllM = useDeleteAllChatThreads()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editingTitle, setEditingTitle] = useState('')
  const [sharingThreadId, setSharingThreadId] = useState<string | null>(null)
  const [exportingId, setExportingId] = useState<string | null>(null)
  const [exportError, setExportError] = useState<string | null>(null)
  const [threadToDelete, setThreadToDelete] = useState<ChatThreadSummary | null>(null)
  const [wipeAllOpen, setWipeAllOpen] = useState(false)
  const [search, setSearch] = useState('')

  const exportThread = async (t: ChatThreadSummary) => {
    if (exportingId) return
    setExportingId(t.id)
    try {
      const safe = (t.title || 'chat').replace(/[^A-Za-z0-9._-]+/g, '_').slice(0, 60)
      await downloadAuthenticatedFile(
        `/api/chatbot/threads/${t.id}/export.pdf`,
        `${safe}.pdf`,
      )
    } catch (e) {
      setExportError(e instanceof Error ? e.message : 'Failed to export chat as PDF.')
    } finally {
      setExportingId(null)
    }
  }

  const allThreads = threadsQ.data?.threads ?? []
  const threads = search.trim()
    ? allThreads.filter((t) => t.title.toLowerCase().includes(search.trim().toLowerCase()))
    : allThreads

  const startNew = () => onSelect(null)

  const [newOpen, setNewOpen] = useState(false)

  // Picker apply handler. We don't create the thread here - we store the
  // chosen visibility on the page and the first message's stream POST
  // creates the thread with it. That avoids the "empty thread in the
  // sidebar" problem and matches how the page already works for private
  // chats.
  const applyPickedVisibility = (v: VisibilityValue) => {
    onPendingVisibilityChange?.(v)
    onSelect(null)
    setNewOpen(false)
  }

  const beginRename = (t: ChatThreadSummary) => {
    setEditingId(t.id)
    setEditingTitle(t.title)
  }

  const commitRename = async () => {
    if (!editingId) return
    const title = editingTitle.trim()
    if (!title) {
      setEditingId(null)
      return
    }
    await renameM.mutateAsync({ id: editingId, title })
    setEditingId(null)
  }

  const performDelete = async () => {
    if (!threadToDelete) return
    const id = threadToDelete.id
    try {
      await deleteM.mutateAsync(id)
      if (activeThreadId === id) onSelect(null)
    } finally {
      setThreadToDelete(null)
    }
  }

  const performWipeAll = async () => {
    try {
      await deleteAllM.mutateAsync()
      onSelect(null)
    } finally {
      setWipeAllOpen(false)
    }
  }

  return (
    <aside className="flex h-full w-[260px] shrink-0 flex-col border-r border-border bg-card">
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Chats
        </span>
        <div className="flex items-center gap-1">
          {/* "+" is a tools-style dropdown when the user can share -
              click to pick the new chat's visibility (Private / Workspace
              / Specific roles). Picking any option queues that visibility
              for the next message and clears the active thread so the
              compose pane opens fresh. Without chatbot.share.all the
              button degrades to a plain "New (private)" trigger. */}
          {canShareAll ? (
            <Popover open={newOpen} onOpenChange={setNewOpen}>
              <PopoverTrigger asChild>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1.5 px-2 text-xs"
                  disabled={createM.isPending}
                  aria-label="New chat - pick visibility"
                  title="New chat"
                >
                  <MessageSquarePlus className="size-3.5" />
                  New
                  <ChevronDown className="size-3 text-muted-foreground" aria-hidden />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 p-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Start a new chat
                </div>
                <VisibilityPicker
                  initial={pendingVisibility ?? { visibility: 'private' }}
                  onApply={applyPickedVisibility}
                  onCancel={() => setNewOpen(false)}
                  applyLabel="Start chat"
                />
              </PopoverContent>
            </Popover>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="h-7 gap-1.5 px-2 text-xs"
              onClick={startNew}
              disabled={createM.isPending}
              aria-label="New private chat"
              title="New chat (private)"
            >
              <MessageSquarePlus className="size-3.5" />
              New
            </Button>
          )}
          {onToggleCollapse ? (
            <Button
              size="icon"
              variant="ghost"
              className="size-7"
              onClick={onToggleCollapse}
              aria-label="Collapse chat history"
              title="Collapse chat history"
            >
              <Menu className="size-3.5" />
            </Button>
          ) : null}
        </div>
      </div>
      {allThreads.length > 0 ? (
        <div className="border-b border-border p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.currentTarget.value)}
              placeholder="Search chats…"
              className="h-7 pl-7 text-xs"
            />
          </div>
        </div>
      ) : null}
      <div className="flex-1 overflow-y-auto py-1">
        {threadsQ.isLoading ? (
          <div className="p-4 text-xs text-muted-foreground">Loading…</div>
        ) : allThreads.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-muted-foreground">
            No chats yet. Start one by asking a question below.
          </div>
        ) : threads.length === 0 ? (
          <div className="px-4 py-6 text-center text-xs text-muted-foreground">
            No chats match &ldquo;{search}&rdquo;.
          </div>
        ) : (
          threads.map((t) => {
            const isActive = t.id === activeThreadId
            const isEditing = t.id === editingId
            return (
              <div
                key={t.id}
                className={cn(
                  'group relative flex flex-col border-l-2 px-2 py-1.5 text-sm transition-colors',
                  isActive
                    ? 'border-primary bg-primary/5'
                    : 'border-transparent hover:bg-accent/60',
                )}
              >
                {isEditing ? (
                  <Input
                    value={editingTitle}
                    onChange={(e) => setEditingTitle(e.currentTarget.value)}
                    onBlur={commitRename}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') commitRename()
                      else if (e.key === 'Escape') setEditingId(null)
                    }}
                    className="h-7 text-xs"
                    autoFocus
                  />
                ) : (
                  <>
                    {/* Title stays on top and uses the full width. */}
                    <button
                      type="button"
                      onClick={() => onSelect(t.id)}
                      className="flex w-full items-center gap-1.5 text-left text-sm font-medium text-foreground"
                    >
                      <span className="truncate">{t.title}</span>
                      {t.visibility === 'tenant' || t.visibility === 'shared' ? (
                        <Users
                          className="size-3 shrink-0 text-success"
                          aria-label={
                            t.visibility === 'tenant'
                              ? 'Shared with the workspace'
                              : 'Shared with selected members'
                          }
                        />
                      ) : null}
                    </button>
                    {/*
                      Metadata line doubles as the hover target for the
                      action icons: on hover the "1h ago · 6 msg" text
                      fades out and the pencil/share/export/delete
                      buttons take its place, so they never overlap the
                      title text.
                    */}
                    <div className="relative mt-0.5 flex h-6 items-center">
                      <button
                        type="button"
                        onClick={() => onSelect(t.id)}
                        className="min-w-0 flex-1 truncate text-left text-[11px] text-muted-foreground transition-opacity group-focus-within:opacity-0 group-hover:opacity-0"
                        tabIndex={-1}
                      >
                        {relativeTime(t.updated_at)} · {t.message_count} msg
                      </button>
                      <div className="absolute inset-y-0 right-0 hidden items-center gap-0.5 group-focus-within:flex group-hover:flex">
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-6 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation()
                          beginRename(t)
                        }}
                        aria-label="Rename chat"
                        title="Rename"
                      >
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-6 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation()
                          setSharingThreadId(t.id)
                        }}
                        aria-label="Share chat"
                        title="Share link"
                      >
                        <Link2 className="size-3.5" />
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-6 text-muted-foreground hover:text-foreground"
                        onClick={(e) => {
                          e.stopPropagation()
                          exportThread(t)
                        }}
                        disabled={exportingId === t.id}
                        aria-label="Export chat as PDF"
                        title="Export as PDF"
                      >
                        {exportingId === t.id ? (
                          <Loader2 className="size-3.5 animate-spin" />
                        ) : (
                          <Download className="size-3.5" />
                        )}
                      </Button>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="size-6 text-muted-foreground hover:text-destructive"
                        onClick={(e) => {
                          e.stopPropagation()
                          setThreadToDelete(t)
                        }}
                        aria-label="Delete chat"
                        title="Delete"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )
          })
        )}
      </div>
      {allThreads.length > 0 ? (
        <div className="border-t border-border p-2">
          <Button
            size="sm"
            variant="ghost"
            className="h-7 w-full justify-start gap-1.5 px-2 text-xs text-muted-foreground hover:text-destructive"
            onClick={() => setWipeAllOpen(true)}
            disabled={deleteAllM.isPending}
          >
            {deleteAllM.isPending ? (
              <Loader2 className="size-3.5 animate-spin" />
            ) : (
              <Trash2 className="size-3.5" />
            )}
            Delete all chats
          </Button>
        </div>
      ) : null}
      <ShareLinkDialog
        open={!!sharingThreadId}
        threadId={sharingThreadId}
        onClose={() => setSharingThreadId(null)}
      />
      <Modal.Alert
        open={!!exportError}
        onOpenChange={(o) => (o ? null : setExportError(null))}
        title="Export failed"
        body={exportError || 'Failed to export chat as PDF.'}
        onClose={() => setExportError(null)}
      />
      <Modal.Confirm
        open={!!threadToDelete}
        onOpenChange={(o) => (o ? null : setThreadToDelete(null))}
        title="Delete this chat?"
        body={
          threadToDelete
            ? `"${threadToDelete.title}" will be permanently removed. This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        confirmVariant="danger"
        onConfirm={performDelete}
        busy={deleteM.isPending}
      />
      <Modal.Confirm
        open={wipeAllOpen}
        onOpenChange={(o) => (o ? null : setWipeAllOpen(false))}
        title="Delete all your chats?"
        body={`This permanently removes every chat you can currently see (${allThreads.length} ${allThreads.length === 1 ? 'chat' : 'chats'}). Other members' private chats are not touched. This cannot be undone.`}
        confirmLabel="Delete all"
        confirmVariant="danger"
        onConfirm={performWipeAll}
        busy={deleteAllM.isPending}
      />
    </aside>
  )
}
